window.BAO = window.BAO || {}
window.BAO.utils = window.BAO.utils || {}

/**
 * Returns the first element that is a descendant of node that
 * matches selectors.
 *
 * @param {String} selector - A DOMString containing one or more selectors to match. This string
 *   must be a valid CSS selector string
 * @param {Object} [config] - You can pass a context to scope the element selection
 * @param {HTMLElement|Document} [context=Document] - The scope we will try and find the element in
 *
 * @returns {HTMLElement} The found element or null
 */
function getElement (selector, { context = document } = {}) {
  return context.querySelector(selector)
}

/**
 * Returns a list of elements that are a descendant of node that
 * matches selectors.
 *
 * @param {String} selector - A DOMString containing one or more selectors to match. This string
 *   must be a valid CSS selector string
 * @param {Object} [config] - You can pass a context to scope the element selection
 * @param {HTMLElement|Document} [context=Document] - The scope we will try and find the element in
 * @param {Boolean} [asArray=true] - Return the elements as an Array instead of an NodeList?
 *
 * @returns {Array|NodeList} The found elements or null
 */
function getElements (selector, { context = document, asArray = true } = {}) {
  const items = context.querySelectorAll(selector)

  return asArray ? Array.from(items) : items
}

/**
 * Checks if the passed string contains any items in an array
 *
 * @param {String} str - The string we will be searching
 * @param {Array} [items] - A list of the items we will be checking for presence in the str
 *
 * @returns {Boolean}
 */
function doesStringContainAny (str, items = []) {
  return items.some(i => str.includes(i))
}

/**
 * Creates an HTMLElement
 *
 * @param {String} tag - What type of element we want this to be
 * @param {Object} [options] - A key / value pair of properties and values we want to set on the
 *   newly created element
 *
 * @returns {HTMLElement}
 */
function createElement (tag, options = {}) {
  const el = document.createElement(tag)

  Object.entries(options).forEach(([key, value]) => {
    el.setAttribute(key, value)
  })

  return el
}

/**
 * Creates an array starting from zero with the length of the passed amount
 *
 * @param {Number} amount - How long do we want this array to be?
 *
 * @returns {Array}
 */
function range (amount) {
  return [...Array(amount).keys()]
}

/**
 * camelCase a string
 *
 * @param {String} str
 *
 * @return {String} value - formatted value
 */
function camelize (str) {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase()
    })
    .replace(/\s+/g, '')
}

/**
 * A Javascript equivalent of Shopify's | img_url filter
 *
 * @param {Object} The properties we will use to create the URL
 * @param {String} src - The Shopify CDN URL of the image
 * @param {String} size - What size we want the image to be
 * @param {null|String} [crop=null] - By default the images aren't cropped but we can choose to
 *   crop them
 * @param {null|Number} [scale=null] - Images are 1x by default, you can choose to pass a different
 *   scale
 *
 * @returns {String}
 */
function resizeImage ({ src, size, crop = null, scale = null } = {}) {
  if (size == null) {
    return src
  }

  if (size === 'master') {
    return src.replace(/http(s)?:/, '')
  }

  const match = src.match(
    /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?/i,
  )

  if (match != null) {
    const prefix = src.split(match[0])
    const suffix = match[0]
    const scaleString = scale ? `@${scale}x` : ''
    const cropString = crop ? `_crop_${crop}` : ''

    return `${prefix[0]}_${size}${cropString}${scaleString}${suffix}`.replace(
      /http(s)?:/,
      '',
    )
  } else {
    return null
  }
}

/**
 * This is the default config we will use when using fetch()
 *
 * @returns {Object}
 */
function getDefaultRequestConfig () {
  return {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }
}

/**
 * Default fetch boilerplate
 * @param {String} url - The URL we are looking to fetch
 * @param {Object} [config] - .fetch config to override the default
 *
 * @returns {Promise}
 */
function fetchJSON (url, config = {}) {
  return fetch(url, Object.assign({}, getDefaultRequestConfig(), config)).then(
    response => {
      if (!response.ok) {
        throw response
      }
      return response.json()
    },
  )
}

/**
 * This will fetch product data from Shopify's AJAX API by handle
 *
 * @param {String} handle - The products handle
 *
 * @returns {Promise}
 */
function fetchProductData (handle) {
  try {
    return fetchJSON(`/products/${handle}.js`)
  } catch (e) {
    console.error(e)
  }
}

/**
 * Gets the offsetTop of the passed element relevant to it's offsetParent
 *
 * @param {HTMLElement|Element} el - The element we'll be getting the offsetTop for
 * @param {Document|HTMLElement} [parent=HTMLBodyElement] - The offsetParent we want to get the offsetTop relative to
 * @param {Number} [offsetTop=0] - The starting offsetTop. This tends to be 0 but given this is a
 *   recursive function, we need to pass it to let it build up
 *
 * @returns {Number}
 */
function getOffsetTop (el, parent = document.body, offsetTop = 0) {
  if (!isNaN(el.offsetTop)) {
    offsetTop += el.offsetTop
  }

  return el.offsetParent === parent
    ? offsetTop
    : getOffsetTop(el.offsetParent, parent, offsetTop)
}

/** Gets the offsetLeft of the passed element relevant to it's offsetParent
 *
 * @param {HTMLElement|Element} el - The element we'll be getting the offsetLeft for
 * @param {Document|HTMLElement} [parent=HTMLBodyElement] - The offsetParent we want to get the
 *   offsetLeft relative to
 * @param {Number} [offsetLeft=0] - The starting offsetLeft. This tends to be 0 but given this is a
 *   recursive function, we need to pass it to let it build up
 *
 * @returns {Number}
 */
function getOffsetLeft (el, parent = document.body, offsetLeft = 0) {
  if (!isNaN(el.offsetLeft)) {
    offsetLeft += el.offsetLeft
  }

  return el.offsetParent === parent
    ? offsetLeft
    : getOffsetLeft(el.offsetParent, parent, offsetLeft)
}

/**
 * Format money values based on your shop currency settings
 * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
 * or 3.00 dollars
 * @param  {String} [format] - shop money_format setting
 *
 * @return {String} value - formatted value
 */
function formatMoney (cents, format = window.theme.moneyFormat) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '')
  }
  let value = ''
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
  const formatString = format

  function formatWithDelimiters (
    number,
    precision = 2,
    thousands = ',',
    decimal = '.',
  ) {
    if (isNaN(number) || number == null) {
      return 0
    }

    number = (number / 100.0).toFixed(precision)

    const parts = number.split('.')
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`,
    )
    const centsAmount = parts[1] ? decimal + parts[1] : ''

    return dollarsAmount + centsAmount
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2)
      break
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0)
      break
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',')
      break
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',')
      break
  }

  return formatString.replace(placeholderRegex, value)
}

function getOrdinalForDay (day) {
  if (day > 3 && day < 21) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

/**
 * Inject variables into a translated string.
 *
 * @param {string} translation
 * @param {{ [variable: string]: any }} variables
 * @param {{ delimiters?: [string, string][] }} [options]
 *
 * @example
 * // returns Hurry! Only 2 left in stock.
 * translateString('Hurry! Only {{ count }} left in stock.', { count: 2 })
 */
function translateString (
  translation,
  variables,
  options = {
    delimiters: [
      ['{{\\s*', '\\s*}}'],
      ['%{', '}'],
    ],
  },
) {
  return Object.entries(variables).reduce((baseTranslation, [key, value]) => {
    const replacePattern = options.delimiters
      .map(([start, end]) => `${start}${key}${end}`)
      .join('|')

    return baseTranslation.replace(new RegExp(replacePattern, 'g'), value)
  }, translation)
}

/**
 * @param {Function} fn
 * @param {number} wait
 *
 * @return {Function}
 */
function debounce (fn, wait) {
  let t

  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), wait)
  }
}

/**
 * @param {FormData} formData
 *
 * @return {Object}
 */
function buildProperties (formData) {
  const propertyKeyValues = [...formData.entries()].filter(
    ([key, value]) => key.includes('properties[') && value !== '',
  )

  /**
   * @param {Object} properties
   * @param {[string, string]} property
   *
   * @returns {Object}
   */
  function propertyReducer (properties, [key, value]) {
    const strippedKey = key.replace('properties[', '').slice(0, -1)

    return {
      ...properties,
      [strippedKey]: value,
    }
  }

  return propertyKeyValues.reduce(
    (properties, property) => propertyReducer(properties, property),
    {},
  )
}

/**
 * Returns the current viewport size.
 * @return {'xs'|'sm'|'md'|'lg'|'xl'}
 */
function viewportSize () {
  const breakpoints = {
    xs: window.matchMedia('(max-width: 767px)').matches,
    sm: window.matchMedia('(min-width: 768px) and (max-width: 900px)').matches,
    md: window.matchMedia('(min-width: 901px) and (max-width: 1200px)').matches,
    lg: window.matchMedia('(min-width: 1201px) and (max-width: 1439px)')
      .matches,
    xl: window.matchMedia('(min-width: 1440px)').matches,
  }
  return Object.keys(breakpoints).find(key => breakpoints[key])
}

function isInViewport (element) {
  const rect = element.getBoundingClientRect()
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

/**
 * Checks if a child is inside a parent
 *
 * @param {HTMLElement} parent
 * @param {HTMLElement} child
 * @param {number} levelsDeep By default 3 - How many levels do you want to check?
 *
 * @returns {Boolean}
 */
function isChild (child, parent, levelsDeep = 3) {
  let node = child.parentNode
  let count = 0

  while (node) {
    if (node === parent) {
      return true
    }

    if (levelsDeep === count || child === parent) {
      return false
    }

    count++
    node = node.parentNode
  }

  return false
}

/**
 * Sleeps until a given condition is met
 *
 * @template {unknown} T
 *
 * @param {() => T} condition
 * @param {number} duration Duration in milliseconds (ms)
 *
 * @returns {Promise<T>}
 */
function sleepUntil (condition, duration = 250) {
  return new Promise(resolve => {
    const interval = setInterval(() => {
      const result = condition()
      if (!result) {
        return
      }

      clearInterval(interval)
      resolve(result)
    }, duration)
  })
}

Object.assign(window.BAO.utils, {
  buildProperties,
  camelize,
  createElement,
  debounce,
  doesStringContainAny,
  fetchJSON,
  fetchProductData,
  formatMoney,
  getDefaultRequestConfig,
  getElement,
  getElements,
  getOffsetTop,
  getOffsetLeft,
  getOrdinalForDay,
  isInViewport,
  viewportSize,
  isChild,
  range,
  resizeImage,
  translateString,
  sleepUntil,
});


/**
 * Returns the first element that is a descendant of node that
 * matches selectors.
 */
function Listeners () {
  this.entries = []
}

/**
 * Keeps track of a listener
 *
 * @param {HTMLElement} element - The element the listener is going to be attached to
 * @param {String} event - The event name we want listen for
 * @param {Function} fn - The function that will get run on the event
 */
Listeners.prototype.add = function add (element, event, fn) {
  this.entries.push({ element, event, fn })

  element.addEventListener(event, fn)
}

/**
 * Stop tracking the specific event
 *
 * @param {String} event - The event name we want listen for
 * @param {Function} fn - The function that will get run on the event
 */
Listeners.prototype.remove = function remove (event, fn) {
  const foundListener = this.entries.find(
    listener => listener.event === event && listener.fn === fn,
  )

  if (foundListener) {
    this.entries = this.entries.filter(listener => listener !== foundListener)

    foundListener.element.removeEventListener(
      foundListener.event,
      foundListener.fn,
    )
  }
}

/**
 * Removes all of the tracked events
 */
Listeners.prototype.removeAll = function removeAll () {
  this.entries = this.entries.filter(listener => {
    listener.element.removeEventListener(listener.event, listener.fn)

    return false
  })
}

Object.assign(window.BAO.utils, {
  Listeners,
});




    window.BAO = window.BAO || {}

window.BAO.CustomElement = function CustomElement ({
  SuperClass = HTMLElement,
} = {}) {
  class CustomElement extends SuperClass {
    /**
     * sections - a list of sections that are related to this element. They can be updated by calling
     * the renderSections method with updated HTML
     * @type {Section[]}
     * @public
     */

    constructor () {
      super()

      this.hasBeenInitialised = false
      this.sections = []
      /** @type {Object.<{ exists: boolean, elements: HTMLElement[], element: HTMLElement }>} */
      this.els = this.getElements(this._getElementIdentifiers())
      /** @type {Object.<{ exists: boolean, elements: HTMLElement[], element: HTMLElement }>} */
      this.externalEls = this.getElements(
        this._getExternalElementIdentifiers(),
        this._externalElIdentifier,
        { context: document },
      )
      this.listeners = new window.BAO.utils.Listeners()

      this.checkValidity()
    }

    connectedCallback () {
      if (this.hasBeenInitialised) return

      this.hasBeenInitialised = true

      this.setupListeners()
    }

    disconnectedCallback () {
      this.listeners.removeAll()
    }

    checkValidity () {}

    raiseError (message) {
      throw new Error(`<${this.localName}>: ${message}`)
    }

    setupListeners () {}

    getElements (
      identifiers,
      elementIdentifier = this._elIdentifier,
      { context = this } = {},
    ) {
      return identifiers.reduce((els, identifier) => {
        const selector =
          this.key && elementIdentifier === this._externalElIdentifier
            ? `[${elementIdentifier}="${identifier}"][key="${this.key}"]`
            : `[${elementIdentifier}="${identifier}"]`
        const elements = Array.from(context.querySelectorAll(selector))

        els[identifier] = {
          exists: elements.length > 0,
          elements,
          element: elements[0],
          selector,
        }

        return els
      }, {})
    }

    _getElementIdentifiers () {
      return Array.from(this.querySelectorAll(`[${this._elIdentifier}]`)).map(
        el => el.getAttribute(this._elIdentifier),
      )
    }

    _getExternalElementIdentifiers () {
      const selector = this.key
        ? `[${this._externalElIdentifier}][key="${this.key}"]`
        : `[${this._externalElIdentifier}]`

      return Array.from(document.querySelectorAll(selector)).map(el =>
        el.getAttribute(this._externalElIdentifier),
      )
    }

    /**
     * Renders multiple sections based on sections returned from the cart AJAX API
     *
     * @param {object} stateSections - An object where the keys are section ids and the values are
     *   the HTML returned from the section rendering API
     *
     * @returns {void}
     */
    renderSections (stateSections) {
      if (this.sections.length === 0) return

      const sectionsToRender = this.sections
        .filter(s => s.id in stateSections)
        .map(s => Object.assign({}, s, { html: stateSections[s.id] }))

      window.BAO.utils.sectionRenderer.renderMultiple(sectionsToRender)

      if (!window.loyaltylion?.ui) return

      window.loyaltylion.ui.refresh()
    }

    log (groupName, ...otherItems) {
      if (!this.debug) return

      /* eslint-disable no-console */
      console.group(groupName)

      otherItems.forEach(item => console.log(item))

      console.groupEnd()
      /* eslint-enable no-console */
    }

    /**
     * @returns {string}
     */
    get identifier () {
      const baseIdentifier = this.hasAttribute('is')
        ? this.getAttribute('is')
        : this.localName.toLowerCase()

      return baseIdentifier
    }

    /**
     * @private
     * @returns {string}
     */
    get _elIdentifier () {
      return `data-${this.identifier}-el`
    }

    /**
     * @private
     * @returns {string}
     */
    get _externalElIdentifier () {
      return `data-${this.identifier}-external-el`
    }

    get debug () {
      return window.theme.isDevTheme
    }

    get key () {
      return this.getAttribute('key')
    }
  }
  return CustomElement
}


    class ProductAccordions extends HTMLElement {
  constructor () {
    super()

    this.itemEls = this.querySelectorAll('[data-el="product-accordions.item"]')

    this.initialiseAccordions()
  }

  initialiseAccordions () {
    this.itemEls.forEach(itemEl => {
      const triggerEls = itemEl.querySelectorAll(
        '[data-el="product-accordions.trigger"]',
      )

      this.handleClick = this.handleClick.bind(this)

      triggerEls.forEach(triggerEl =>
        triggerEl.addEventListener('click', this.handleClick),
      )
    })
  }

  handleClick (event) {
    const accordionItem = event.target.closest(
      '[data-el="product-accordions.item"]',
    )

    const currentState = accordionItem.getAttribute('aria-expanded') === 'true'

    accordionItem.setAttribute('aria-expanded', !currentState)

    accordionItem.classList.remove('prd-Accordion_Item-partial')
  }
}

customElements.define('product-accordions', ProductAccordions)



    class ProductAccordionsHorizontal extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.handleTabChange = this.handleTabChange.bind(this)

    this.externalEls.trigger?.elements.forEach(triggerEl => {
      this.listeners.add(triggerEl, 'click', this.handleTabChange)
    })
    this.els.trigger?.elements.forEach(triggerEl => {
      this.listeners.add(triggerEl, 'click', this.handleTabChange)
    })
  }

  handleTabChange (event) {
    const clickedTrigger = event.target

    this.els.trigger?.elements.forEach(triggerEl => {
      triggerEl.setAttribute(
        'aria-pressed',
        triggerEl.dataset.tab === clickedTrigger.dataset.tab,
      )
    })

    this.els.panel?.elements.forEach(panelEl => {
      panelEl.setAttribute(
        'aria-hidden',
        panelEl.dataset.tab !== clickedTrigger.dataset.tab,
      )
    })
  }
}

if (!customElements.get('product-accordions-horizontal')) {
  customElements.define(
    'product-accordions-horizontal',
    ProductAccordionsHorizontal,
  )
}


class ReadMoreDescription extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['trigger', 'content']
  }

  connectedCallback () {
    super.connectedCallback()
    this.setInitialHeight()
  }

  setupListeners () {
    super.setupListeners()
    this.listeners.add(
      this.els.trigger.element,
      'click',
      this.handleClick.bind(this),
    )
  }

  handleClick (event) {
    if (this.isExpanded) {
      this.els.content.element.setAttribute('aria-expanded', 'false')
      this.els.trigger.element.textContent = this.dataset.readMoreTranslationReadMore
    } else {
      this.els.content.element.setAttribute('aria-expanded', 'true')
      this.els.trigger.element.textContent = this.dataset.readMoreTranslationReadLess
    }
  }

  setInitialHeight () {
    const contentStyle = getComputedStyle(this.els.content.element)

    let lineHeight = parseFloat(contentStyle.lineHeight)

    let marginBottom = 0
    const listItems = this.els.content.element.querySelectorAll('li')

    listItems.forEach((li, index) => {
      if (index < 3) {
        lineHeight = parseFloat(getComputedStyle(li).lineHeight)
        marginBottom += parseFloat(getComputedStyle(li).marginBottom)
      }
    })

    this.initialHeight = lineHeight * 3.75 + marginBottom
    if (!this.isExpanded) {
      this.els.content.element.style.setProperty(
        '--cutoff',
        `${this.initialHeight}px`,
      )
    }
  }

  get isExpanded () {
    return this.els.content.element.getAttribute('aria-expanded') === 'true'
  }
}

if (!customElements.get('read-more-description')) {
  customElements.define('read-more-description', ReadMoreDescription)
}
